import { GridRowSelectionModel } from "@mui/x-data-grid-pro";
import { useCallback, useState } from "react";
import { useTreinamentoCargoPagination } from "../hooks/TreinamentoCargo_cadastro/pagination";
import { processError } from "../../../../common/utils/base_error";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { HabilidadeHabilidadeCargo } from "../../domain/models/habilidade_habilidade_cargo";
import { CargoHabilidadeCargo } from "../../domain/models/cargo_habilidade_cargo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";

/**
 * LoadCargosState
 */

type LoadCargosState = {
  idTreinamento?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useHabilidadeCargoCadastroViewModel
 */

export const useHabilidadeCargoCadastroViewModel = () => {
  const {
    rh: {
      getAllCargosForHabilidadesCargosUseCase,
      getAllHabilidadesForHabilidadesCargosUseCase,
      saveCargoHabilidadeUseCase,
    },
  } = useDependencyInjection();

  const { setPageSaving, pushErrorNotification, pushInfoNotification } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [habilidades, setHabilidades] = useState<HabilidadeHabilidadeCargo[]>();
  const [cargos, setCargos] = useState<CargoHabilidadeCargo[]>();

  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>();
  const [selectedHabilidade, setSelectedHabilidade] = useState<HabilidadeHabilidadeCargo>();

  const pagination = useTreinamentoCargoPagination();

  /**
   * Funções de carregamento
   */

  const loadHabilidades = useCallback(async () => {
    try {
      const result = await getAllHabilidadesForHabilidadesCargosUseCase.getAllHabilidades();
      setHabilidades(result);
    } catch (e: unknown) {
      setHabilidades([]);
      throw e;
    }
  }, [
    getAllHabilidadesForHabilidadesCargosUseCase,
  ]);

  const loadCargos = useCallback(async (funcState?: LoadCargosState) => {
    try {
      const filteredIdTreinamento = funcState?.idTreinamento ?? selectedHabilidade?.idHabilidade ?? 0;

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getAllCargosForHabilidadesCargosUseCase.getAllCargos(
        filteredIdTreinamento,
        filteredPage,
        filteredRowsPerPage
      );

      setCargos(getAllCargosForHabilidadesCargosUseCase.items);
      setSelectionModel({
        type: 'include',
        ids: new Set(getAllCargosForHabilidadesCargosUseCase.items.filter(x => x.checked).map(x => x.idCargo)),
      });

      pagination.setRowsPerPage(getAllCargosForHabilidadesCargosUseCase.pageSize);
      pagination.setTotalItems(getAllCargosForHabilidadesCargosUseCase.totalItems);
    } catch (e: unknown) {
      setCargos([]);
      throw e;
    }
  }, [
    selectedHabilidade,
    pagination,
    getAllCargosForHabilidadesCargosUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);

      await loadHabilidades();

      if (selectedHabilidade) {
        await loadCargos();
      }
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar habilidades!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleSubmit = useCallback(async () => {
    try {
      setLoading(true);
      setSaving(true);
      setPageSaving(true);

      if (selectedHabilidade && selectionModel) {
        const cargoIds: number[] = [];

        for (var value of selectionModel.ids.values()) {
          if (typeof value === 'number') {
            cargoIds.push(value as number);
          } else if (typeof value === 'string') {
            cargoIds.push(Number(value));
          }
        }

        await saveCargoHabilidadeUseCase.saveCargoHabilidade(
          selectedHabilidade.idHabilidade,
          cargoIds,
        );

        await loadHabilidades();
        await loadCargos();

        pushInfoNotification("Sucesso", "Alterações salvas com sucesso!", { isAutoFinish: true });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setSaving(false);
      setLoading(false);
      setPageSaving(false);
    }
  }, [
    selectedHabilidade,
    selectionModel,
    saveCargoHabilidadeUseCase,
    loadHabilidades,
    loadCargos,
    setPageSaving,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);

      await loadHabilidades();

      if (selectedHabilidade) {
        await loadCargos();
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    selectedHabilidade,
    loadHabilidades,
    loadCargos,
    pushErrorNotification,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useTreinamentoCargoPagination.getState().setPage(page + 1);
      await loadCargos({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCargos,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useTreinamentoCargoPagination.getState().setPage(1);
      useTreinamentoCargoPagination.getState().setRowsPerPage(rowsPerPage);
      await loadCargos({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCargos,
    pushErrorNotification,
  ]);

  const handleCargoRowSelectionChange = useCallback((models: GridRowSelectionModel) => {
    setSelectionModel(models);
  }, []);

  const handleTreinamentoSelect = useCallback(async (habilidade: HabilidadeHabilidadeCargo | null) => {
    try {
      setLoading(true);
      if (habilidade) {
        useTreinamentoCargoPagination.getState().setPage(1);
        setSelectedHabilidade(habilidade);
        await loadCargos({ idTreinamento: habilidade.idHabilidade, page: 1 });
      } else {
        setSelectedHabilidade(undefined);
        setCargos(undefined);
        setSelectionModel({ type: 'include', ids: new Set([]) });
      }
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar treinamentos!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadCargos,
    pushErrorNotification,
  ]);

  return {
    loading,
    saving,
    errorMessage,
    habilidades,
    cargos,
    selectionModel,
    selectedHabilidade,
    pagination,
    handleSubmit,
    handleReload,
    handlePageChange,
    handleRowsPerPageChange,
    handleCargoRowSelectionChange,
    handleTreinamentoSelect,
  };
}